import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useIdleTimer } from 'react-idle-timer';
import { logOut, isUserAuthenticated } from 'services/localstorage';
import { useHistory, useLocation } from 'react-router-dom';
import { routes } from 'routes/routes';
import { axiosInstance } from 'services/api';
import { toast } from 'react-toastify';
import { getUserProfile } from 'api/user';
import AuthService from 'services/authService';

export const UserContext = createContext({
  user: {},
  updateData: () => {},
  error: undefined,
});

const UserProvider = ({ children }) => {
  const [user, setUser] = useState({});
  const [error, setError] = useState(undefined);
  const [authInitialized, setAuthInitialized] = useState(false);
  const history = useHistory();
  const location = useLocation();

  const updateData = (data) => {
    setUser({ ...user, ...data });
  };

  const handleError = useCallback(
    (e) => {
      if (
        (location.pathname.includes('2fa') ||
          location.pathname.includes('login')) &&
        e.response?.status === 403
      ) {
        // eslint-disable-next-line no-useless-return
        return;
      }
      if (e?.message === 'Network Error') {
        setError(e);
      } else if (e?.code === 'ECONNABORTED') {
        setError(e);
      } else if (
        e?.response?.status === 404 &&
        !(e?.config?.url.includes('poster') && e?.config?.method === 'get')
      ) {
        setError(e);
      } else if (
        e?.response?.status === 403 ||
        e?.response?.status === 500 ||
        e?.response?.status === 400
      ) {
        setError(e);
      }
    },
    [history, location.pathname],
  );

  useEffect(() => {
    const requestInterceptor = axiosInstance.interceptors.request.use((req) => {
      setError(undefined);
      return req;
    });

    const responseInterceptor = axiosInstance.interceptors.response.use(
      (res) => res,
      (e) => {
        handleError(e);
        throw e;
      },
    );

    return () => {
      axiosInstance.interceptors.request.eject(requestInterceptor);
      axiosInstance.interceptors.response.eject(responseInterceptor);
    };
  }, [handleError, location.pathname]);

  const handleDismissError = () => setError(undefined);

  const isDebug = () => localStorage.getItem('idle_debug') === 'true';

  const handleOnIdle = () => {
    if (isDebug()) console.log('[IDLE DEBUG] onIdle');
    logOut();
    updateData({ active: false });
    history.push(routes.LOGIN);
  };

  const handleOnActive = () => {
    if (isDebug()) console.log('[IDLE DEBUG] onActive');
  };

  const handleOnAction = (event) => {
    if (isDebug())
      console.log(`[IDLE DEBUG] onAction: ${event?.type || 'unknown'}`);
  };

  const { reset, pause, getRemainingTime } = useIdleTimer({
    timeout: 1000 * 60 * 60 * 3, // 3 hours
    onIdle: handleOnIdle,
    onActive: handleOnActive,
    onAction: handleOnAction,
    debounce: 500,
    stopOnIdle: true,
    crossTab: true,
    syncTimers: 200,
  });

  const debugIntervalRef = useRef(null);

  const startDebugInterval = useCallback(() => {
    if (debugIntervalRef.current) return;
    debugIntervalRef.current = setInterval(() => {
      if (localStorage.getItem('idle_debug') !== 'true') {
        clearInterval(debugIntervalRef.current);
        debugIntervalRef.current = null;
        return;
      }
      const seconds = Math.ceil(getRemainingTime() / 1000);
      console.log(`[IDLE DEBUG] remaining: ${seconds}s`);
    }, 1000);
  }, [getRemainingTime]);

  useEffect(() => {
    if (isDebug()) startDebugInterval();

    const onStorage = (e) => {
      if (e.key === 'idle_debug') {
        if (e.newValue === 'true') {
          startDebugInterval();
        } else if (debugIntervalRef.current) {
          clearInterval(debugIntervalRef.current);
          debugIntervalRef.current = null;
        }
      }
    };

    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      if (debugIntervalRef.current) {
        clearInterval(debugIntervalRef.current);
        debugIntervalRef.current = null;
      }
    };
  }, [startDebugInterval]);

  const resetRef = useRef(reset);
  const pauseRef = useRef(pause);

  useEffect(() => {
    resetRef.current = reset;
    pauseRef.current = pause;
  }, [reset, pause]);

  useEffect(() => {
    if (user.active) {
      resetRef.current();
    } else if (user.active === false) {
      pauseRef.current();
    }
  }, [user.active]);

  useEffect(() => {
    if (error) {
      let errorMessage = error?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = `Error${error?.status ? ` ${error?.status}` : ''}: ${
          error?.message || 'Something went wrong..'
        }. Please try again in a few minutes.`;
      }

      toast.error(errorMessage, {
        onClose: handleDismissError,
        autoClose: 5000,
        pauseOnFocusLoss: true,
      });
    }
  }, [error, error?.message, error?.status]);

  const getProfile = async () => {
    const { data, error: getUserProfileError } = await getUserProfile();

    if (getUserProfileError) {
      toast.error(getUserProfileError.message);
      return;
    }

    setUser((prevState) => ({
      ...prevState,
      profile: { ...data, predictions_access: true }, // TODO: delete predictions_access
    }));
  };

  useEffect(() => {
    const publicPages = [
      routes.LOGIN,
      routes.MULTI_FACTOR,
      routes.REGISTRATION,
      routes.REGISTRATION_COMPLETED,
      routes.RESET_PASSWORD,
      routes.RESET_PASSWORD_CHECK_EMAIL,
      routes.SETUP_PASSWORD,
      routes.FORGOT_CREDENTIALS,
      routes.NEW_PASSWORD_EMAIL_REDIRECT,
    ];
    const isPublicPage = publicPages.includes(window.location.pathname);

    if (isPublicPage) {
      setAuthInitialized(true);
      return;
    }

    AuthService.initializeFromCookie().then(() => {
      setAuthInitialized(true);
      if (isUserAuthenticated()) {
        updateData({ active: true });
        getProfile();
      }
    });
  }, []);

  useEffect(() => {
    if (user.active && !user.profile) {
      getProfile();
    }
  }, [user.active]);

  // Wait for auth initialization before rendering children
  if (!authInitialized) {
    return null;
  }

  return (
    <UserContext.Provider value={{ user, updateData, error }}>
      {children}
    </UserContext.Provider>
  );
};

UserProvider.propTypes = { children: PropTypes.any };

export default UserProvider;
